import { type FC, useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react';

import { useTheme } from '@mui/material/styles';
import { BrowserHistory } from '@vakantiesnl/components/src/__LEGACY__/atoms/BrowserHistory';
import { StructuredData } from '@vakantiesnl/components/src/__LEGACY__/atoms/StructuredData';
import { AccommodationSectionHeading } from '@vakantiesnl/components/src/atoms/AccommodationSectionHeading';
import { Button } from '@vakantiesnl/components/src/atoms/Button';
import { Content } from '@vakantiesnl/components/src/atoms/Content';
import { Link } from '@vakantiesnl/components/src/atoms/Link';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { AccommodationBlock } from '@vakantiesnl/components/src/blocks/AccommodationBlock';
import { AccommodationContactBlock } from '@vakantiesnl/components/src/blocks/AccommodationContactBlock';
import { AccommodationInfoContainer } from '@vakantiesnl/components/src/blocks/AccommodationInfoContainer';
import { AccommodationHeader } from '@vakantiesnl/components/src/molecules/AccommodationHeader';
import { AccommodationStickyOffer } from '@vakantiesnl/components/src/molecules/AccommodationStickyOffer';
import ImageComponent from '@vakantiesnl/components/src/molecules/ImageComponent';
import { ImageOverview } from '@vakantiesnl/components/src/molecules/ImageOverview';
import { type MapProps } from '@vakantiesnl/components/src/molecules/Map';
import { PriceCard } from '@vakantiesnl/components/src/molecules/PriceCard';
import { RatingSubscores } from '@vakantiesnl/components/src/molecules/RatingSubscores';
import { USPs } from '@vakantiesnl/components/src/molecules/USPs/USPs';
import { AccommodationDescriptionSection } from '@vakantiesnl/components/src/organisms/AccommodationDescriptionSection';
import { AccommodationReviews } from '@vakantiesnl/components/src/organisms/AccommodationReviews/AccommodationReviews';
import { SimilarAccos } from '@vakantiesnl/components/src/organisms/SimilarAccos';
import {
	getAccommodationBreadCrumbs,
	getAccommodationSchemaItem,
	getReviewSchemaItem,
	isZoover,
} from '@vakantiesnl/components/src/utils';
import { getSubscores } from '@vakantiesnl/components/src/utils/accommodation/getSubScores';
import { isOfferWithZooverDeal } from '@vakantiesnl/components/src/utils/accommodation/zooverDealHelpers';
import {
	AccommodationSections,
	MENU_OFFSET,
	MENU_SCROLL_DURATION,
} from '@vakantiesnl/components/src/utils/accoSections';
import { message } from '@vakantiesnl/components/src/utils/message';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { useTripFilterContext } from '@vakantiesnl/services/src/context/useTripFilterContext';
import { type AdlinkValues } from '@vakantiesnl/services/src/gtm/adlinks';
import { type Geo } from '@vakantiesnl/services/src/gtm/util/useMapGeo';
import { useAsset } from '@vakantiesnl/services/src/hooks/contentful';
import { useGetAdlinks } from '@vakantiesnl/services/src/hooks/queries/useGetAdlinks';
import { useGetReviews } from '@vakantiesnl/services/src/hooks/queries/useGetReviews';
import { useAccommodationUrlBuilder } from '@vakantiesnl/services/src/hooks/useAccommodationUrlBuilder';
import { useAccoOfferStore, useApplicationStore, useGetCheapestOffer } from '@vakantiesnl/services/src/stores';
import { isPackageOffer } from '@vakantiesnl/services/src/util';
import { inject } from '@vakantiesnl/services/src/util/template';
import { type BreadCrumbItem, type Nbc, type Search } from '@vakantiesnl/types';
import { type Adlink } from '@vakantiesnl/types/src/adlinks';
import { type DestinationType } from '@vakantiesnl/types/src/elasticsearch/entities';
import dayjs from 'dayjs';
import { isNull, isUndefined, pickBy } from 'lodash';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { Element, scroller } from 'react-scroll';
import { type BreadcrumbList, type ListItem } from 'schema-dts';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { useStyles } from './AccommodationOverview.style';

const AdlinksContainer = dynamic(
	() =>
		import('@vakantiesnl/components/src/molecules/AdlinksContainer').then((module) => ({
			default: module.AdlinksContainer,
		})),
	{
		ssr: false,
	},
);

const ImageModal = dynamic(
	() => import('@vakantiesnl/components/src/molecules/ImageModal').then((module) => ({ default: module.ImageModal })),
	{
		ssr: false,
	},
);

const ModalOrDrawer = dynamic(
	() =>
		import('@vakantiesnl/components/src/molecules/ModalOrDrawer/ModalOrDrawer').then((module) => ({
			default: module.ModalOrDrawer,
		})),
	{
		ssr: false,
	},
);

const ShareAccoModal = dynamic(
	() =>
		import('@vakantiesnl/components/src/__LEGACY__/molecules/ShareAccoModal').then((module) => ({
			default: module.ShareAccoModal,
		})),
	{
		ssr: false,
	},
);

type Props = {
	offer?: string;
	accommodation: Search.FullAccommodationItem;
	googleAnalyticsCookie?: string;
};

const IMAGES_TO_SHOW = 5;

const MapModal = dynamic(() => import('@vakantiesnl/components/src/organisms/MapModal').then((mod) => mod.MapModal), {
	ssr: false,
});

const getBreadcrumbsSchemaItem = (breadcrumbs: BreadCrumbItem[], origin: string): BreadcrumbList => {
	return {
		'@type': 'BreadcrumbList',
		itemListElement: breadcrumbs.map((breadcrumb, position: number) => {
			return pickBy(
				{
					'@type': 'ListItem',
					position: position + 1,
					name: breadcrumb.text,
					item: breadcrumb.to && origin && `${origin}${breadcrumb.to}`,
				},
				(val) => !isUndefined(val),
			) as unknown as ListItem;
		}),
	} as unknown as BreadcrumbList;
};

export const AccommodationOverview: FC<Props> = ({ offer, accommodation, googleAnalyticsCookie }) => {
	const theme = useTheme();
	const { classes, cx } = useStyles();
	const microCopies = useMicroCopyContext();
	const { isDesktop, isMobile } = useBreakpoints();
	const router = useRouter();

	const currentCheapestOffer = useGetCheapestOffer(accommodation);

	const isLoadingOffers = useAccoOfferStore((s) => s.isDepartureDateLoading);
	const totalDepartureDateOffers = useAccoOfferStore((s) => s.totalDepartureDateOffers);
	const hasOffers = totalDepartureDateOffers > 0;

	const { staticCanonicalOrigin: origin } = useApplicationStore((s) => s);
	const { state } = useTripFilterContext();
	const { reviewsData } = useGetReviews({ id: accommodation.id });
	const { adlinks } = useGetAdlinks({ id: accommodation.heliosId });
	const mapImageUrl = useAsset(microCopies['accommodation.map.assetId'])?.fields?.file?.url;

	const accommodationUrl = useAccommodationUrlBuilder(router, {
		...state,
		page: reviewsData?.pages[Math.max(reviewsData.pages.length - 1, 0)].pageParam.page,
	});

	const [isImageModalOpen, setIsImageModalOpen] = useState(false);
	const [selectedImageIndex, setSelectedImageIndex] = useState(0);
	const [isAdlinkPopperOpen, setIsAdlinkPopperOpen] = useState(false);
	const [isMapModalOpen, setIsMapModalOpen] = useState(false);
	const [center, setCenter] = useState<MapProps['center'] | null>(null);
	const [GTMformatedGeo, setGTMformatedGeo] = useState<Geo | undefined>(undefined);
	const [shareAccoModalIsOpen, setShareAccoModalIsOpen] = useState(false);
	const [isPriceInView, setIsPriceInView] = useState(false);
	const [isDetailView, setIsDetailView] = useState(false);

	const totalReviewsCount = reviewsData?.pages[0].total ?? 0;
	const accommodationPhotos = accommodation.images;
	const topPhotos = accommodation.topImages;
	const accommodationPhotosCount = accommodation.images.length;
	const bookableAccommodation = accommodation.isBookable || accommodation.isBookableQenner;
	const hasZooverDeal = !!currentCheapestOffer && isOfferWithZooverDeal(currentCheapestOffer);
	const [isAdlinksOpenedFromLastImageCta, setIsAdlinksOpenedFromLastImageCta] = useState<boolean>();
	const lastYear: number = dayjs().subtract(1, 'year').year();

	const handleImageModalClose = useCallback(() => {
		setSelectedImageIndex(0);
		setIsImageModalOpen(false);
	}, []);

	const handleReviewCountClick = useCallback(
		(event: MouseEvent) => {
			if (typeof window !== 'undefined') {
				event.stopPropagation();
				event.preventDefault();
				if (isImageModalOpen) {
					handleImageModalClose();
				}
				scroller.scrollTo(AccommodationSections.Reviews, {
					duration: MENU_SCROLL_DURATION,
					smooth: true,
					offset: MENU_OFFSET,
				});
				return;
			}
		},
		[isImageModalOpen, handleImageModalClose],
	);

	const reviewCountLink = inject(microCopies['ratings.score.template'], {
		reviewCountLink: isZoover ? (
			<Link
				onClick={handleReviewCountClick}
				key={accommodation.reviewCount}
				className={classes.reviewCount}
				aria-label="Reviews"
			>
				{message(microCopies['accommodation.review'], {
					count: accommodation.reviewCount,
				})}
			</Link>
		) : (
			<>
				{message(microCopies['accommodation.review'], {
					count: accommodation.reviewCount,
				})}
			</>
		),
	});

	const adlinksToShow = useMemo<Adlink[]>(() => adlinks ?? [], [adlinks]);

	const addReviewUrl = useMemo(
		() => `/review?entityLevel=accommodation&entityId=${accommodation.heliosId}`,
		[accommodation.heliosId],
	);

	const accoLastYearAward = useMemo(() => accommodation?.awards[lastYear], [accommodation?.awards, lastYear]);

	useEffect(() => {
		if (router.isReady && state.hasLoadedInitialState) {
			if (Object.values(state.selected).filter(Boolean).length) {
				scroller.scrollTo(AccommodationSections.AvailabilityOffers, {
					duration: MENU_SCROLL_DURATION,
					smooth: true,
					offset: -MENU_OFFSET,
				});
			} else {
				window.scrollTo({ top: 0, behavior: 'smooth' });
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [router.isReady, state.hasLoadedInitialState]);

	/** Memoize mapped images for using the ImageComponent with cloudImg */
	const mappedBucketImageUrls: Nbc.MappedBucketImgProps[] = useMemo(
		() =>
			accommodationPhotos?.length > 0
				? accommodationPhotos.map((image) => {
						return {
							url: image.bucketUrl,
							rotationDegrees:
								!isNull(image.rotationDegrees) && image.rotationDegrees !== 0
									? image.rotationDegrees
									: undefined,
						};
					})
				: [],
		[accommodationPhotos],
	);

	/** Map topImages */
	const mappedBucketTopImageUrls: Nbc.MappedBucketImgProps[] = useMemo(
		() =>
			topPhotos && topPhotos.length > 0
				? topPhotos.map((image) => {
						return {
							url: image.url,
							rotationDegrees:
								!isNull(image.rotationDegrees) && image.rotationDegrees !== 0
									? image.rotationDegrees
									: undefined,
						};
					})
				: [],
		[topPhotos],
	);

	/** Setting the currently viewed image index and tracking image view event */
	const handleImageChange = useCallback(
		(index: number) => {
			setSelectedImageIndex(index);
			if (accommodationPhotos[index] && index === selectedImageIndex) {
				/** Tracking image view event */
				import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
					import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ imageView }) =>
						track(
							imageView(
								accommodation,
								currentCheapestOffer,
								undefined,
								selectedImageIndex,
								accommodationPhotos?.length > 0
									? accommodationPhotos[selectedImageIndex]?.id
									: undefined,
								adlinksToShow.length,
							),
						),
					),
				);
			}
		},
		[accommodationPhotos, selectedImageIndex, accommodation, currentCheapestOffer, adlinksToShow.length],
	);

	/* Tracking the image modal click*/
	const imageModalClickTrackingEvent = useCallback(() => {
		import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
			import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ trackImageModalClick }) =>
				track(trackImageModalClick(accommodation, isImageModalOpen)),
			),
		);
	}, [accommodation, isImageModalOpen]);

	const handleImageClick = useCallback(
		(index: number) => {
			/* Every time an image is clicked, there will be image tracking */
			imageModalClickTrackingEvent();

			/** Open the image modal */
			if (index === IMAGES_TO_SHOW - 1) {
				/** When clicking on the last image don't open the detail view  */
				setIsDetailView(false);
				setIsImageModalOpen(true);
			} else if (accommodationPhotos[index]) {
				/** Set the selected image index to open in the detail view */
				setSelectedImageIndex(index);
				setIsDetailView(true);
				setIsImageModalOpen(true);
			}
		},
		[accommodationPhotos, imageModalClickTrackingEvent],
	);

	/** On mount dispatch the accommodation pageview tracking */
	useEffect(() => {
		import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
			import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ accommodationPageView }) =>
				track(accommodationPageView(accommodation, offer, undefined, adlinksToShow.length, hasZooverDeal)),
			),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [adlinksToShow.length, hasZooverDeal]);

	const viewOffersEvent = useCallback(
		(event: MouseEvent<HTMLButtonElement>, isLastImageCtaClicked?: boolean) => {
			const { currentTarget } = event;
			const isPriceCardBookBtnClicked = currentTarget.id === 'priceCardBookBtn';
			const isImageModalHeaderBookBtn = currentTarget.id === 'imageHeaderBookBtn';
			const isNonBookableProvidersBtnClicked = currentTarget.ariaLabel === microCopies['adlinks.showAll'];

			import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
				import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ viewOffersClick }) =>
					track(
						viewOffersClick(
							accommodation,
							bookableAccommodation
								? currentTarget.ariaLabel || currentTarget.id
								: isNonBookableProvidersBtnClicked
									? microCopies['adlinks.showAll']
									: microCopies['adlinks.viewPrices'],
							adlinksToShow.length,
							isLastImageCtaClicked,
							isPriceCardBookBtnClicked,
							isImageModalHeaderBookBtn,
							isNonBookableProvidersBtnClicked,
						),
					),
				),
			);
		},
		[accommodation, bookableAccommodation, microCopies, adlinksToShow.length],
	);

	const viewPartnersEvent = useCallback(() => {
		setIsMapModalOpen(false);
		setIsAdlinkPopperOpen(true);

		import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
			import('@vakantiesnl/services/src/gtm/maps').then(({ onViewPartnersFromMapModal }) =>
				track(
					onViewPartnersFromMapModal(
						accommodation,
						microCopies['adlinks.showAll'],
						adlinksToShow.length,
						true,
					),
				),
			),
		);
	}, [accommodation, microCopies, adlinksToShow.length]);

	const onBreadCrumbClickEvent = useCallback(
		(
			destinationUrl: string,
			destinationType?: DestinationType,
			isAccoHeader?: boolean,
			atomType?: string,
			label?: string,
		) => {
			import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
				import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ breadCrumbClick }) =>
					track(
						breadCrumbClick(
							destinationUrl,
							accommodation.heliosId,
							destinationType,
							isAccoHeader,
							atomType,
							label,
						),
					),
				),
			);
		},
		[accommodation],
	);

	const onClickNonBookableCta = useCallback((isLastImageCtaClicked?: boolean) => {
		setIsAdlinksOpenedFromLastImageCta(isLastImageCtaClicked);
		/** Delay the opening of the adlinks modal/drawer until the image modal finishes closing */
		setTimeout(() => {
			/** Open adlinks modal/drawer */
			setIsAdlinkPopperOpen(true);
		}, 400);
	}, []);

	const onAdlinkPopperClose = useCallback(() => {
		setIsAdlinkPopperOpen(false);
		setIsAdlinksOpenedFromLastImageCta(false);
	}, []);

	// Keep the accommodation URL in sync with the selected filters
	useDeepCompareEffect(() => {
		if (!accommodationUrl || !state.hasLoadedInitialState) return;

		/** Turn off scrolling on nav. This should be handled by child components fe. reviews */
		router.replace(
			accommodationUrl.href,
			{ ...accommodationUrl.as, hash: accommodationUrl.hash },
			{ scroll: false, shallow: true },
		);
	}, [accommodationUrl, state.hasLoadedInitialState]);

	const formattedAccoSchemaItem = useMemo(() => {
		const accommodationShema = getAccommodationSchemaItem(
			accommodation,
			origin,
			microCopies,
			currentCheapestOffer,
			accommodation.previewImage,
		);
		if (reviewsData?.pages[0].reviews.length) {
			Object.assign(accommodationShema, { review: reviewsData?.pages[0].reviews.map(getReviewSchemaItem) });
		}
		return accommodationShema;
	}, [accommodation, origin, microCopies, currentCheapestOffer, reviewsData]);

	const formattedAccoBreadCrumbs = useMemo(
		() => getAccommodationBreadCrumbs(accommodation, microCopies),
		[accommodation, microCopies],
	);

	const breadCrumbsSchemaItem = useMemo(() => {
		if (formattedAccoBreadCrumbs) {
			return getBreadcrumbsSchemaItem(formattedAccoBreadCrumbs, origin);
		}
	}, [formattedAccoBreadCrumbs, origin]);

	const trackAdlinksPhotosModal = useCallback(
		(adlinkValues: AdlinkValues) => {
			import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
				import('@vakantiesnl/services/src/gtm/adlinks').then(({ trackAdlinksPhotosModal }) =>
					track(trackAdlinksPhotosModal(adlinkValues, accommodation, isAdlinksOpenedFromLastImageCta)),
				),
			);
		},
		[accommodation, isAdlinksOpenedFromLastImageCta],
	);

	const toggleShareAccoModal = useCallback(
		(event?: MouseEvent<HTMLAnchorElement | HTMLButtonElement>, isAccoHeader?: boolean) => {
			event?.preventDefault();

			setShareAccoModalIsOpen((isOpen) => {
				if (!isOpen) {
					import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
						import('@vakantiesnl/services/src/gtm/accommodationPage').then(
							({ onShareAccoPageButtonClick }) =>
								track(onShareAccoPageButtonClick(accommodation, window.location.href, isAccoHeader)),
						),
					);
				}

				return !isOpen;
			});
		},
		[accommodation],
	);

	const openMapModal = useCallback(() => {
		setIsMapModalOpen(true);
		import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
			import('@vakantiesnl/services/src/gtm/maps').then(({ onMapModalToggle }) =>
				track(onMapModalToggle(accommodation, 'open', GTMformatedGeo, adlinksToShow.length)),
			),
		);
	}, [accommodation, GTMformatedGeo, adlinksToShow.length]);

	const onMapModalClose = useCallback(() => {
		setIsMapModalOpen(false);
		import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
			import('@vakantiesnl/services/src/gtm/maps').then(({ onMapModalToggle }) =>
				track(onMapModalToggle(accommodation, 'close', GTMformatedGeo, adlinksToShow.length)),
			),
		);
	}, [accommodation, GTMformatedGeo, adlinksToShow.length]);

	const onMapBookNowClick = useCallback(() => {
		setIsMapModalOpen(false);

		/** Scroll to the offers */
		scroller.scrollTo(AccommodationSections.Availability, {
			offset: MENU_OFFSET,
			smooth: true,
			duration: MENU_SCROLL_DURATION,
		});

		import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
			import('@vakantiesnl/services/src/gtm/maps').then(({ onViewOffersFromMapModal }) =>
				track(
					onViewOffersFromMapModal(
						accommodation,
						microCopies['common.bookNow'],
						GTMformatedGeo,
						adlinksToShow.length,
						true,
					),
				),
			),
		);
	}, [accommodation, microCopies, GTMformatedGeo, adlinksToShow.length]);

	const handleToggleImageView = useCallback(() => {
		setIsDetailView(!isDetailView);

		import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
			import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ toggleImageModalView }) =>
				track(toggleImageModalView(accommodation)),
			),
		);
	}, [accommodation, isDetailView]);

	useEffect(() => {
		if (accommodation.geo.latitude && accommodation.geo.longitude) {
			setCenter({
				lat: accommodation.geo.latitude,
				lng: accommodation.geo.longitude,
			});
		} else {
			setCenter(null);
		}
	}, [accommodation.geo.latitude, accommodation.geo.longitude]);

	useEffect(() => {
		if (accommodation) {
			setGTMformatedGeo({
				country_name: accommodation?.country,
				region_name: accommodation?.region,
				city_name: accommodation?.city,
			});
		}
	}, [accommodation]);

	const handleOnMapCardClick = useCallback(() => {
		if (accommodation.isBookable || accommodation.isBookableQenner) {
			return onMapBookNowClick();
		} else if (!!adlinksToShow?.length && adlinksToShow?.length > 0) {
			return viewPartnersEvent();
		}
	}, [
		accommodation.isBookable,
		accommodation.isBookableQenner,
		adlinksToShow?.length,
		onMapBookNowClick,
		viewPartnersEvent,
	]);

	/** Don't add a mapCardButton when the acco is not bookable or doesn't have adlinks to show */
	const shouldShowMapCardButton = useMemo(
		() =>
			!!accommodation.isBookable ||
			!!accommodation.isBookableQenner ||
			!!(!!adlinksToShow?.length && adlinksToShow?.length > 0),
		[accommodation.isBookable, accommodation.isBookableQenner, adlinksToShow?.length],
	);

	const subscores = useMemo(
		() => (accommodation?.reviewPerAspectGroup ? getSubscores(accommodation.reviewPerAspectGroup) : null),
		[accommodation.reviewPerAspectGroup],
	);

	return (
		<>
			{center && GTMformatedGeo && isMapModalOpen && (
				<MapModal
					accommodation={accommodation}
					center={center}
					offerItem={currentCheapestOffer}
					isDesktop={isDesktop}
					isMobile={isMobile}
					isMapModalOpen={isMapModalOpen}
					onCardClick={shouldShowMapCardButton ? handleOnMapCardClick : undefined}
					onMapModalClose={onMapModalClose}
				/>
			)}
			<StructuredData schemaItem={formattedAccoSchemaItem} id="acco-info" isDataDynamic={true} />
			<BrowserHistory />
			{breadCrumbsSchemaItem && <StructuredData schemaItem={breadCrumbsSchemaItem} id="breadcrumbItem" />}
			<AccommodationHeader
				microCopies={microCopies}
				accommodation={accommodation}
				toggleShareAccoModal={toggleShareAccoModal}
				onBreadCrumbClick={onBreadCrumbClickEvent}
				wrapperClassName={classes.headerWrapper}
				isImageModalOpen={isImageModalOpen}
				adlinksCount={adlinksToShow?.length}
			/>
			<Content className={classes.contentContainer}>
				<Element
					name={AccommodationSections.Pictures}
					className={cx(classes.elementContainer, classes.galleryContainer, {
						[classes.hasZooverDeal]: hasZooverDeal,
					})}
				>
					<ImageOverview
						images={mappedBucketTopImageUrls}
						imagesToShow={IMAGES_TO_SHOW}
						onImageClick={handleImageClick}
						imagesCount={accommodationPhotosCount}
						microCopies={microCopies}
						rating={accommodation.rating}
						reviewCountLink={reviewCountLink}
					/>
				</Element>
				<Element name={AccommodationSections.Overview} className={classes.elementContainer}>
					<div className={classes.overviewAndPriceBlockWrapper}>
						<div className={classes.priceCardWrapper} data-cy={'pricecard-overview-container'}>
							<div className={classes.stickyPricecard}>
								<PriceCard
									microCopies={microCopies}
									isBookable={bookableAccommodation}
									accommodationAdlinks={adlinksToShow}
									accommodation={accommodation}
									offer={currentCheapestOffer}
									offerIsLoading={isLoadingOffers}
									hasOffers={hasOffers}
									viewOffersEvent={viewOffersEvent}
									googleAnalyticsCookie={googleAnalyticsCookie}
									setIsPriceInView={setIsPriceInView}
								/>
							</div>
						</div>

						<div className={classes.overviewWrapper}>
							<USPs
								accommodation={accommodation}
								discountAmount={currentCheapestOffer?.discountAmount}
								discountAmountType={
									isPackageOffer(currentCheapestOffer)
										? currentCheapestOffer?.discountAmountType
										: undefined
								}
								isAccoPage={true}
								accoLastYearAward={accoLastYearAward}
							/>
							{isZoover && subscores && <RatingSubscores subscores={subscores} />}
							{!!accommodation.description && (
								<div>
									<AccommodationSectionHeading>
										{inject(microCopies['accommodation.info.title'], {
											accommodation: accommodation.title,
										}).join('')}
									</AccommodationSectionHeading>

									<AccommodationDescriptionSection accommodation={accommodation} withScrollButton />
								</div>
							)}
							{center && GTMformatedGeo && (
								<div>
									<AccommodationSectionHeading>
										{microCopies['accommodation.map.title']}
									</AccommodationSectionHeading>
									<Link className={classes.mapContainer} onClick={openMapModal}>
										{mapImageUrl && (
											<ImageComponent
												src={mapImageUrl}
												fill={true}
												className={classes.mapImage}
												alt={microCopies['common.clickToOpenMapModalAltText']}
												role="presentation"
												sizes={
													'(max-width: ' + theme.breakpoints.values.md + 'px) 100vw, 784px'
												}
											/>
										)}
										<Button
											variant="secondary"
											className={classes.openMapButton}
											asText
											size={isDesktop ? 'medium' : 'small'}
											leadingIcon="pin"
										>
											{microCopies['accommodation.maps.showOnMap']}
										</Button>
									</Link>
								</div>
							)}

							{accommodation.premiumAdlinkEnabled && (
								<div>
									<AccommodationSectionHeading>
										{microCopies[`contactInformation.address`]}
									</AccommodationSectionHeading>
									<AccommodationContactBlock
										accommodation={accommodation}
										isPremium={true}
										showTitle={false}
									/>
								</div>
							)}
						</div>
					</div>
				</Element>
				{!bookableAccommodation && (
					<Element name={AccommodationSections.SimilarAccos} className={classes.elementContainer}>
						<SimilarAccos accommodation={accommodation} />
					</Element>
				)}
				{/** AccommodationBlock includes the BookingModule/TripFilters */}
				{bookableAccommodation && state.hasLoadedInitialState && (
					<Element name={AccommodationSections.Availability} className={classes.elementContainer}>
						<AccommodationBlock accommodation={accommodation} isPriceInView={isPriceInView} />
					</Element>
				)}
				<Element name={AccommodationSections.Facilities} className={classes.noMargin}>
					<AccommodationInfoContainer accommodation={accommodation} />
				</Element>
				{isZoover && (
					<Element name={AccommodationSections.Reviews}>
						<AccommodationReviews
							total={totalReviewsCount}
							microCopies={microCopies}
							addReviewUrl={addReviewUrl}
							adlinks={adlinksToShow}
						/>
					</Element>
				)}
				{bookableAccommodation && (
					<Element name={AccommodationSections.SimilarAccos} className={classes.elementContainer}>
						<SimilarAccos accommodation={accommodation} />
					</Element>
				)}
			</Content>
			{accommodationPhotos && mappedBucketImageUrls && isImageModalOpen && (
				<ImageModal
					open={isImageModalOpen}
					isDetailView={isDetailView}
					toggleView={handleToggleImageView}
					images={mappedBucketImageUrls}
					onCloseClick={handleImageModalClose}
					accommodation={accommodation}
					toggleShareAccoModal={toggleShareAccoModal}
					viewOffersEvent={viewOffersEvent}
					toggleAdlinkModal={onClickNonBookableCta}
					adlinksCount={adlinksToShow?.length}
					onImageChange={handleImageChange}
					selectedImageIndex={selectedImageIndex}
					microCopies={microCopies}
					offer={currentCheapestOffer}
				/>
			)}
			{shareAccoModalIsOpen && (
				<ShareAccoModal onClickOutside={toggleShareAccoModal} accommodation={accommodation} />
			)}

			{isAdlinkPopperOpen && (
				<ModalOrDrawer
					open={isAdlinkPopperOpen}
					title={
						!isDesktop
							? microCopies['adlinks.drawer.heading']
							: microCopies['adlinks.hybridproductpage.heading']
					}
					hasCloseIcon={true}
					onClose={onAdlinkPopperClose}
				>
					<AdlinksContainer
						adlinks={adlinksToShow as Adlink[]}
						accommodationName={accommodation.title}
						toggleVariant="secondary"
						showExpanded={true}
						showDefaultDescription={false}
						descriptionToShow={
							<Typography variant="bodyMd" as="p">
								{microCopies['adlinks.popper.description']}
							</Typography>
						}
						onAdlinkClick={trackAdlinksPhotosModal}
					/>
				</ModalOrDrawer>
			)}
			{!bookableAccommodation && (
				<AccommodationStickyOffer
					microCopies={microCopies}
					accommodation={accommodation}
					adlinksCount={adlinksToShow.length}
				/>
			)}
		</>
	);
};
